import { Grid, InputAdornment } from '@mui/material';
import { Typography } from '@mui/material';
import { TextField } from '@mui/material';
import { Stack } from '@mui/material';
import { FormField, RadioGroupField } from 'components';
import { Formik } from 'formik';
import { RootState } from 'store';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  VerificationTechniquesEnum,
  VerificationTechniquesInputListTypes,
  VerificationTechniquesInputTypes,
  VerificationTechniquesType
} from 'types/verification';
import { convertVerificationExpressPattern } from 'utils/imageViewerUtils';
import Yup from 'utils/yup';
import useFabricCanvas from 'hooks/useFabricCanvas';
import { setCurrentFieldLabel } from 'store/reducers/taggingSlice';
import { ENABLE_RUBBERBAND_ASSURANCE } from 'pages/design/document/configuration/constants';
import IconButton from 'components/@extended/IconButton';
import { HighlightAltOutlined } from '@mui/icons-material';
import { setCurrentFieldName, setHeaderPatternTextRoi, setPatternTextRoi } from 'store/reducers/columnSlice';
import { useTheme } from '@mui/material';
import { ThemeMode } from 'types/config';

interface Props {
  techniqueType: VerificationTechniquesType;
  rectImageContent: string | null;
  selectedFieldName?: string;
  patternFormRef: any;
  initialData: any;
  isEdit: boolean;
  currentPageNumber?: any;
  setshowDialog?: any;
  disableLabelRubberband?: any;
  setShowLabelRubberbandDialog?: any;
  onCancel?: any;
  setAddEditPatternType?: any;
  headerData?: any;
}

const FieldsByPatterns = ({
  rectImageContent,
  techniqueType,
  selectedFieldName,
  currentPageNumber,
  patternFormRef,
  initialData,
  setshowDialog,
  disableLabelRubberband,
  setShowLabelRubberbandDialog,
  onCancel,
  setAddEditPatternType,
  isEdit,
  headerData
}: Props) => {
  const onChangeExpression = (e: ChangeEvent<HTMLInputElement>, setFieldValue: any) => {
    const data = convertVerificationExpressPattern(e.target.value);
    setFieldValue('expressionPattern', data.expressionPattern);
    setFieldValue('expressionRegex', data.expressionRegex);
  };

  const dispatch = useDispatch();
  const theme = useTheme();

  const columnName = useSelector((state: RootState) => state.columnSlice.columnName);
  const headerName = useSelector((state: RootState) => state.columnSlice.HeaderPatternTextRoi);
  const footerNmae = useSelector((state: RootState) => state.columnSlice.PatternTextRoi);
  const currentFieldName = useSelector((state: RootState) => state.columnSlice.currentFieldName);
  const [patternType, setPatterType] = useState(currentFieldName ? 'disabled' : 'enabled');

  useEffect(() => {
    if (currentFieldName === 'sectionFooter') {
      dispatch(setPatternTextRoi(headerData ? headerData?.patternTextFromROI : initialData?.patternTextFromROI));
      patternFormRef.current?.setFieldValue('sectionFooter', headerData ? headerData?.patternTextFromROI : initialData?.patternTextFromROI);
    }
    if (currentFieldName === 'sectionHeader') {
      dispatch(setHeaderPatternTextRoi(headerData ? headerData?.patternTextFromROI : initialData?.patternTextFromROI));
      patternFormRef.current?.setFieldValue('sectionHeader', headerData ? headerData?.patternTextFromROI : initialData?.patternTextFromROI);
    }
  }, [initialData, headerData]);
  useEffect(() => {
    if (currentFieldName === 'sectionHeader') {
      dispatch(setHeaderPatternTextRoi(headerData ? headerData?.patternTextFromROI : initialData?.patternTextFromROI));
      patternFormRef.current?.setFieldValue('sectionHeader', headerData ? headerData?.patternTextFromROI : initialData?.patternTextFromROI);
    }
  }, [patternType]);
  const { enableLabelRubberband } = useFabricCanvas({
    currentPageNumber: currentPageNumber ?? (currentPageNumber || 1)
  });
  const handleLabelRubberband = (fieldName: string, fieldLabel: string) => {
    if (setShowLabelRubberbandDialog) {
      setShowLabelRubberbandDialog(false);
    }
    if (setAddEditPatternType) {
      setAddEditPatternType(false);
    }

    dispatch(setCurrentFieldName(fieldName));
    dispatch(setCurrentFieldLabel(fieldLabel));
    enableLabelRubberband();
  };

  const InputListObj: VerificationTechniquesInputListTypes = useMemo(
    () => ({
      [VerificationTechniquesEnum.KEY_VALUE_PAIR]: [
        {
          label: 'Pattern',
          name: 'patternText',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        }
      ],
      [VerificationTechniquesEnum.EXPRESSION]: [
        {
          label: 'Expression Pattern',
          name: 'expressionPattern',
          onChange: onChangeExpression,
          defaultValue: initialData?.pattern
        },
        {
          label: 'Expression Sample',
          name: 'expressionRegex',
          readOnly: true,
          defaultValue: convertVerificationExpressPattern(initialData?.pattern)?.expressionRegex
        }
      ],
      [VerificationTechniquesEnum.LOOKUP]: [
        {
          label: 'Document Pattern',
          name: 'documentPattern',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        },
        {
          label: 'Display Name',
          name: 'displayValue',
          defaultValue: initialData?.displayValue
        }
      ],
      [VerificationTechniquesEnum.Nested_Key_Printed]: [
        {
          label: 'Pattern',
          name: 'patternText',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        },
        {
          label: 'Section Header',
          name: 'sectionHeader',
          defaultValue: isEdit ? initialData?.pattern : headerName
        },
        {
          label: 'Section Footer',
          name: 'sectionFooter',
          defaultValue: footerNmae
        }
      ],
      [VerificationTechniquesEnum.TABLE]: [
        {
          label: 'Document Pattern',
          name: 'documentPattern',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        }
      ],
      [VerificationTechniquesEnum.TABLE_PRINTED]: [
        {
          label: 'Document Pattern',
          name: 'documentPattern',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        }
      ],
      [VerificationTechniquesEnum.Nested_Lookup_Printed]: [
        {
          label: 'Document Pattern',
          name: 'documentPattern',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        },
        {
          label: 'Display Name',
          name: 'displayValue',
          defaultValue: initialData?.displayValue
        },
        {
          label: 'Section Header',
          name: 'sectionHeader',
          defaultValue: isEdit ? initialData?.pattern : headerName
        },
        {
          label: 'Section Footer',
          name: 'sectionFooter',
          defaultValue: footerNmae
        }
      ],

      [VerificationTechniquesEnum.Context_Based]: [
        {
          label: 'Value',
          name: 'patternText',
          defaultValue: isEdit ? initialData?.pattern : initialData?.patternTextFromROI
        }
      ]
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialData, isEdit]
  );

  const inputList: VerificationTechniquesInputTypes[] | null = useMemo(() => {
    const inputs = InputListObj[techniqueType];

    if (!inputs) {
      return null;
    }

    if (techniqueType === VerificationTechniquesEnum.Nested_Key_Printed) {
      return patternType === 'enabled'
        ? inputs.filter((input) => input.name === 'patternText')
        : inputs.filter((input) => input.name !== 'patternText');
    }

    if (techniqueType === VerificationTechniquesEnum.Nested_Lookup_Printed) {
      return patternType === 'enabled'
        ? inputs.filter((input) => input.name === 'documentPattern' || input.name === 'displayValue')
        : inputs.filter((input) => input.name !== 'documentPattern' && input.name !== 'displayValue');
    }

    return inputs;
  }, [techniqueType, patternType, InputListObj]);

  const initialValues: any = useMemo(() => {
    if (!inputList) return {};

    const data = inputList.reduce((acc, input) => {
      acc[input.name] = input.defaultValue;
      return acc;
    }, {} as Record<string, any>);

    const hasSectionFields = inputList.some((item) => item.name === 'sectionHeader' || item.name === 'sectionFooter');

    data.Type = hasSectionFields ? 'disabled' : 'enabled';

    return data;
  }, [inputList]);

  return (
    <Stack gap={2}>
      {/* {techniqueType !== VerificationTechniquesEnum.TABLE && ( */}

      {/* )} */}

      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          pattern: Yup.string()
        })}
        onSubmit={() => undefined}
        innerRef={patternFormRef}
      >
        {({ setFieldValue }) => (
          <form>
            {techniqueType === VerificationTechniquesEnum.Nested_Lookup_Printed ||
            techniqueType === VerificationTechniquesEnum.Nested_Key_Printed ? (
              <Grid mb={2}>
                <RadioGroupField
                  name="Type"
                  required
                  label="Type"
                  options={[
                    { label: 'Pattern', value: 'enabled' },
                    { label: 'Section', value: 'disabled' }
                  ]}
                  onChange={(e, value) => {
                    setFieldValue('Type', value);
                    setPatterType(value);
                    if (value === 'disabled') {
                      dispatch(setCurrentFieldName('sectionHeader'));
                    }
                  }}
                  onChangeOverride
                />
              </Grid>
            ) : null}

            <Grid container alignItems={'center'} columnSpacing="30px" mb={2}>
              <Grid item md={4}>
                <Typography>Name</Typography>
              </Grid>
              <Grid item md={8}>
                <Typography className="finish-file-modal-label" sx={{ backgroundColor: theme.palette.background.default }}>
                  {columnName ? columnName : selectedFieldName}
                </Typography>
              </Grid>
            </Grid>
            <Stack gap="30px">
              {inputList &&
                inputList.map((input) => (
                  <Grid key={input.name} container alignItems={'center'} columnSpacing="30px">
                    <Grid item md={4}>
                      <Typography>{input.label}</Typography>
                    </Grid>
                    <Grid item md={8}>
                      <FormField
                        className="user-selection"
                        name={input.name}
                        label=""
                        placeholder=""
                        component={TextField}
                        InputProps={{
                          ...(ENABLE_RUBBERBAND_ASSURANCE.includes(input.name) && {
                            endAdornment: (
                              <InputAdornment position="end" className="editableField">
                                <IconButton
                                  size="small"
                                  shape="rounded"
                                  color="secondary"
                                  onClick={(e: any) => {
                                    setshowDialog(true);

                                    e.stopPropagation();
                                    handleLabelRubberband(input.name, input.label);
                                  }}
                                >
                                  <HighlightAltOutlined />
                                </IconButton>
                              </InputAdornment>
                            )
                          }),
                          readOnly: input.readOnly,
                          disabled: input.disabled,
                          sx: {
                            backgroundColor: theme.palette.background.paper
                          }
                        }}
                        {...(input.onChange
                          ? {
                              onChange: (e: ChangeEvent<HTMLInputElement>) => input.onChange && input.onChange(e, setFieldValue)
                            }
                          : {})}
                        autoComplete="off"
                      />
                    </Grid>
                  </Grid>
                ))}
            </Stack>
          </form>
        )}
      </Formik>

      <Grid container alignItems={'center'} columnSpacing="30px">
        <Grid item md={4}>
          <Typography>Snippet</Typography>
        </Grid>
        <Grid item md={8}>
          {rectImageContent && <img src={rectImageContent} alt="cavas piece" width="100%" />}
        </Grid>
      </Grid>
    </Stack>
  );
};

export default FieldsByPatterns;
